import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ApiError } from '../api/client'
import type { ErrDetail } from '../api/types'
import { copyText } from '../lib/clipboard'
import { IconClose, IconInbox } from './icons'

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: string }) {
  return <span className={`badge badge-${tone}`}>{children}</span>
}

export const statusTone = (s: string): string =>
  s === 'IN_STOCK' ? 'ok' : s === 'SOLD' ? 'sold' : s === 'WITHDRAWN' ? 'muted' : 'warn'

export const batchTone = (s: string): string =>
  s === 'COMMITTED' ? 'ok' : s === 'UNDONE' ? 'muted' : s === 'UNDOING' || s === 'NOT_APPLIED' ? 'warn' : 'neutral'

const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'

// 逗号分隔的选择器串不能直接拼前缀：只有第一段会被限定作用域。
const inBody = (scope: string) => FOCUSABLE.split(',')
  .map((s) => `${scope} ${s.trim()}`)
  .join(',')

/**
 * Modal 负责焦点：打开时落进内容区第一个可操作控件（确认框里就是「请输入 X」
 * 那一格），Tab 在框内循环，关闭后把焦点还给打开它的那个按钮。
 * 少了这一步，键盘用户在破坏性操作弹窗里会 Tab 到背后的表格上。
 */
export function Modal({
  title,
  onClose,
  children,
  width,
}: {
  title: string
  onClose: () => void
  children: ReactNode
  width?: number
}) {
  const box = useRef<HTMLDivElement>(null)
  // onClose 每次渲染都是新函数（ConfirmDialog 在 busy 时换成空操作），
  // 所以用 ref 持有，effect 只在挂载时跑一次：否则每敲一个字焦点都会被抢走。
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    const node = box.current
    // [data-autofocus] 让调用方指定落点（例如"一次点击即可确认"的对话框把焦点
    // 放在确认键上，回车就能执行）；否则进内容区第一个可操作控件。
    const first = node?.querySelector<HTMLElement>('[data-autofocus]') ??
      node?.querySelector<HTMLElement>(inBody('.modal-body')) ??
      node
    first?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeRef.current()
        return
      }
      if (e.key !== 'Tab' || !node) return
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      )
      if (items.length === 0) return
      const firstEl = items[0]
      const lastEl = items[items.length - 1]
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault()
        firstEl.focus()
      }
    }

    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      opener?.focus?.()
    }
  }, [])

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="modal"
        ref={box}
        tabIndex={-1}
        style={width ? { maxWidth: width } : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header>
          <h2>{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="关闭">
            <IconClose size={16} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  )
}

/**
 * Notice renders API errors. A failed write is never shown as a bare "失败":
 * the code, request id and per-domain reasons are what let an operator decide
 * whether to retry the same key or stop and look at the batch.
 */
export function Notice({ error, extra }: { error: unknown; extra?: ReactNode }) {
  if (!error) return extra ? <div className="notice info">{extra}</div> : null
  if (!(error instanceof ApiError)) return <div className="notice error">{String(error)}</div>
  return (
    <div className={`notice ${error.status === 409 ? 'warn' : 'error'}`} role="alert">
      <strong>
        {error.message}
        <span className="code">{error.code}</span>
      </strong>
      {error.requestId && <div className="sub">请求编号 {error.requestId}</div>}
      {error.code === 'NETWORK' && <div className="sub">结果未知：请点"重试查询结果"，切勿改用新的操作。</div>}
      {error.conflictDetails.length > 0 && <DetailTable details={error.conflictDetails} />}
      {extra}
    </div>
  )
}

export function DetailTable({ details }: { details: ErrDetail[] }) {
  return (
    <table className="table compact">
      <thead>
        <tr>
          <th>域名</th>
          <th>原因</th>
          <th>说明</th>
        </tr>
      </thead>
      <tbody>
        {details.slice(0, 50).map((d, i) => (
          <tr key={i}>
            <td className="mono">{d.domain || d.field || '-'}</td>
            <td>
              <code>{d.reason || '-'}</code>
            </td>
            <td>{d.detail || '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * Pager 支持三件事：上下页、每页条数、直接跳页。
 * onSize 不给就不渲染每页条数选择器 —— 审计/批次/下单那几页沿用旧的简版，
 * 不必为了库存页的需求被一起改掉。
 */
export function Pager({
  page,
  size,
  total,
  onPage,
  onSize,
  sizeOptions = [20, 50, 100, 200],
}: {
  page: number
  size: number
  total: number
  onPage: (p: number) => void
  onSize?: (n: number) => void
  sizeOptions?: number[]
}) {
  const pages = Math.max(1, Math.ceil(total / size))
  const [jump, setJump] = useState('')
  function goJump() {
    const n = Number(jump)
    if (!Number.isInteger(n) || n < 1) return
    onPage(Math.min(pages, n))
    setJump('')
  }
  return (
    <nav className="pager" aria-label="分页">
      {onSize && (
        <label className="pager-size">
          <span>每页</span>
          <select value={String(size)} onChange={(e) => onSize(Number(e.target.value))} aria-label="每页显示条数">
            {sizeOptions.map((n) => (
              <option key={n} value={String(n)}>
                {n} 条
              </option>
            ))}
          </select>
        </label>
      )}
      <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        上一页
      </button>
      <span aria-live="polite">
        第 {page} / {pages} 页 · 共 {total} 条
      </span>
      <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        下一页
      </button>
      <span className="pager-jump">
        <span>前往</span>
        <input
          type="number"
          min={1}
          max={pages}
          value={jump}
          onChange={(e) => setJump(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              goJump()
            }
          }}
          aria-label="跳转到指定页码"
        />
        <button type="button" onClick={goJump} disabled={jump === ''}>
          跳转
        </button>
      </span>
    </nav>
  )
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <em>{hint}</em>}
    </label>
  )
}

export function Empty({ text }: { text: string }) {
  return (
    <div className="empty">
      <IconInbox />
      <span>{text}</span>
    </div>
  )
}

/**
 * CopyListDialog：出库/恢复成功之后弹出的结果窗，把这次动过的域名一行一条列出来，
 * 带一个「复制」按钮。操作员拿到这份清单就能直接粘进下游表格或发给客户，
 * 不用再回列表页按勾选状态找一遍。
 *
 * 复制格式是纯文本一行一个 —— 和「入库」页的粘贴格式一致，两处能直接对接。
 */
export function CopyListDialog({
  title,
  items,
  note,
  onClose,
}: {
  title: string
  items: string[]
  note?: ReactNode
  onClose: () => void
}) {
  const [copied, setCopied] = useState<null | 'ok' | 'fail'>(null)

  async function copy() {
    setCopied((await copyText(items.join('\n'))) ? 'ok' : 'fail')
  }

  return (
    <Modal title={title} onClose={onClose} width={560}>
      <div className="row spread copy-head">
        <span className="muted">共 {items.length} 个域名</span>
        <button type="button" onClick={() => void copy()} data-autofocus>
          {copied === 'ok' ? '已复制 ✓' : copied === 'fail' ? '复制失败，请手动选' : '复制'}
        </button>
      </div>
      {note && <p className="sub">{note}</p>}
      <div className="copy-list mono" role="region" aria-label="域名清单" tabIndex={0}>
        {items.map((d, i) => (
          <div key={i}>{d}</div>
        ))}
      </div>
      <div className="row end">
        <button type="button" className="primary" onClick={onClose}>
          知道了
        </button>
      </div>
    </Modal>
  )
}

export const fmtTime = (iso?: string): string => (iso ? new Date(iso).toLocaleString('zh-CN', { hour12: false }) : '-')

/** 表格里「更新」列的短格式：到分钟就够，秒数只会把列撑宽、操作员不会看。 */
export const fmtStamp = (iso?: string): string => {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}
